import { Container, DrawerV2, Overlay } from '@cleartrip/ct-design-components'
import React, { useCallback, useEffect, useState } from 'react'

import { getSrpSearchData , setServicesLoading } from '@/redux/slices/srpSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store/hooks'

import { generateSrpAdditionalPayload, ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import { getServicesData } from '@/api/srp'
import FlightDetailsBody from '@/components/srp/FlightDetails/FlightDetailsBody'
import FlightDetailsFooter from '@/components/srp/FlightDetails/FlightDetailsFooter'
import FlightDetailsHeader from '@/components/srp/FlightDetails/FlightDetailsHeader'
import FlightDetailsTab from '@/components/srp/FlightDetails/FlightDetailsTab'
import PerAdultTooltip from '@/components/srp/Tupple/Domestic/CommonComponents/PerAdultTooltip'
import { Card, pricingDetailsType, QueryParamsData, showFlightDetailsType } from '@/types/srp'
import { replacePlaceholders } from '@/utils'
import { isEmpty } from '@/utils/general/browserHelper'
import { getCheapestFareInCard, getFarePricingAttributes, getFaresInfo, getIntlTuppleTags, getSpecialRTDetails } from '@/utils/srp/helper'
import { PTC_FARE_CATEGORY } from '@/constants/components/fareType'
import { ProcessedData } from '@/utils/srp/types'

interface FlightDetailsProps {
    flightDetails: showFlightDetailsType
    setFlightDetails: (ar1: showFlightDetailsType) => void
    showPerAdultPricing: boolean
    onDomesticTuppleClick: (arg1: Card) => void
    onInternationalBookClick: () => void
    searchQuery: Partial<QueryParamsData>
    pricingDetails: Partial<pricingDetailsType>
    isTripDetails: boolean
    isCheckInBaggageEnabled: boolean
    onwardFilterPtcType: keyof typeof PTC_FARE_CATEGORY | ''
    returnFilterPtcType: keyof typeof PTC_FARE_CATEGORY | ''
}

const FlightDetails = ({
    flightDetails,
    setFlightDetails = () => {},
    searchQuery = {},
    showPerAdultPricing = false,
    onDomesticTuppleClick = () => {},
    onInternationalBookClick = () => {},
    pricingDetails = {},
    isTripDetails = false,
    isCheckInBaggageEnabled = false,
    onwardFilterPtcType = '',
    returnFilterPtcType = ''
}: FlightDetailsProps) => {
    const [selectedTab, setSelectedTab] = useState(0)
    const [perAdultTooltip, setPerAdultTooltip] = useState(<></>)
    const { searchData } = useAppSelector(getSrpSearchData)
    const { subTravelOptions = {}, fares = {}, searchIntent = {}, dataId = '' } = searchData || {}
    const { J1: { paxCriteria = [] } = {} } = searchIntent || {}
    const [flightBenefitsData, setFlightBenefitsData] = useState({onWardsData: {}, returnData: {}})
    const [baggageData, setBaggageData] = useState({onWardsBaggageData: {}, returnBaggageData: {}})
    const [isSpecialRT, setIsSpecialRT] = useState(false)
    const [specialRtFareList, setSpecialRtFareList] = useState([])
    const dispatch = useAppDispatch()
    const isRoundTrip = !isEmpty(searchQuery?.return_date)
    const isIntl = searchQuery?.intl === 'y'

    const handlePerAdultPricing = useCallback(() => {
        try {
            if (showPerAdultPricing && !isEmpty(flightDetails.cardDetails)) {
                const selectedFares = getCheapestFareInCard(flightDetails.cardDetails, subTravelOptions, fares, isCheckInBaggageEnabled, onwardFilterPtcType, returnFilterPtcType) || []
                let totalBaseFare = 0
                let totalTax = 0
                let totalPrice = 0
                selectedFares?.forEach(fare => {
                    const {
                        totalBaseFare: baseFare,
                        totalTax: tax,
                        totalPrice: price
                    } = getFarePricingAttributes({ fare, showPerAdultPricing: false })
                    totalBaseFare += baseFare
                    totalTax += tax
                    totalPrice += price
                })
                const tooltipContent = (
                    <PerAdultTooltip
                        paxCriteria={paxCriteria}
                        totalBaseFare={totalBaseFare}
                        totalPrice={totalPrice}
                        totalTax={totalTax}
                    />
                )
                setPerAdultTooltip(tooltipContent)
            } else {
                setPerAdultTooltip(<></>)
            }
        } catch (e) {
            // Error handling for per adult pricing calculation
        }
    }, [showPerAdultPricing, flightDetails.cardDetails, subTravelOptions, fares, paxCriteria])

    const checkSpecialRTStatus = () => {
        try {
            // Only check for international round-trip cases
            if (!isIntl || !isRoundTrip || flightDetails?.cardDetails?.length !== 2) {
                setIsSpecialRT(false)
                setSpecialRtFareList([])
                return
            }

            const [onwardFlight, returnFlight] = flightDetails.cardDetails
            const selectedFares = getCheapestFareInCard(flightDetails.cardDetails, subTravelOptions, fares, isCheckInBaggageEnabled, onwardFilterPtcType, returnFilterPtcType) || []
            const [onwardFare, returnFare] = selectedFares

            const { specialRTFareMap = {} } = searchData || {}

            const specialRTResult = getSpecialRTDetails({
                selectedFares: [onwardFare, returnFare],
                selectedFlights: [onwardFlight, returnFlight],
                specialRTFareMap,
                fares,
                showPerAdultPricing,
                isCheckInBaggageEnabledOw: false,
                isCheckInBaggageEnabledRt: false
            })
            const { isSpecialRT: specialRTStatus, specialRtFareList: fareList } = specialRTResult
            setIsSpecialRT(specialRTStatus)
            setSpecialRtFareList(fareList || [])
        } catch (error) {
            setIsSpecialRT(false)
            setSpecialRtFareList([])
        }
    }
    
    const fetchBenefitsForFlightDetails = useCallback(() => {
        if(!isIntl || !flightDetails?.show) return
        const makeServiceCall = async (card: Card, journeyKey: string, paxInfos: Array<{ paxType: string; paxCount: number }>) => {
            try {
                const { travelOptionId, summary } = card
                const { firstDeparture: { airport: { time = '', zoneId = '' } = {} } = {} } = summary || {}
                const departureTime = {
                    time,
                    zoneId
                }
                const ptcFareType = journeyKey === 'J1' ? onwardFilterPtcType : returnFilterPtcType

                // Get only the cheapest fare for this card
                const cheapestFares = getCheapestFareInCard([card], subTravelOptions, fares, isCheckInBaggageEnabled, ptcFareType) || []
                const cheapestFare = cheapestFares[0]
                
                if (!cheapestFare) {
                    return
                }

                const { fareId, pricing, subTravelOptionId = '', subTravelOptionKey = '' } = cheapestFare || {}
                
                // Use fixed requiredBenefitTypes as per new requirement
                const requiredBenefitTypes = [ 'BAGGAGE','PENALTIES']

                const fareObj = {
                    fareId,
                    perPaxTotalPrice: pricing?.perAdultPricing?.totalPrice || pricing?.totalPricing?.totalPrice,
                    requiredBenefitTypes
                }

                const subTravelOptionData = {
                    subTravelOptionId,
                    subTravelOptionKey,
                    isInternational: isIntl,
                    departureTime,
                    fares: [fareObj]
                }

                const payload = {
                    dataId,
                    travelOptions: {
                        [travelOptionId]: {
                            travelOptionId,
                            subTravelOptions: [subTravelOptionData],
                            paxInfos
                        }
                    }
                }

                // Make API call
                dispatch(setServicesLoading(true))
                const response = await getServicesData(payload)
                const dataKey = journeyKey === 'J1' ? 'onWardsData' : 'returnData'
                setFlightBenefitsData(prev => ({
                    ...prev,
                    [dataKey]: response
                }))
            } catch (e) {
                // Error handling for service call
            } finally {
                dispatch(setServicesLoading(false))
            }
        }

        try {
            if (isEmpty(flightDetails?.cardDetails)) return
            // Construct paxInfos from passenger criteria
            const paxInfos = paxCriteria.map((paxDetails: { type: string; count: number }) => ({
                paxType: paxDetails.type,
                paxCount: paxDetails.count
            }))

            if (isRoundTrip) {
                // For roundtrip, make separate calls for onward (J1) and return (J2)
                flightDetails.cardDetails.forEach((card, index) => {
                    const journeyKey = index === 0 ? 'J1' : 'J2'
                    makeServiceCall(card, journeyKey, paxInfos)
                })
            } else {
                // For one-way, make single call for J1
                const card = flightDetails.cardDetails[0]
                if (card) {
                    makeServiceCall(card, 'J1', paxInfos)
                }
            }
        } catch (e) {
            // Error handling for fetch benefits
        }
    }, [flightDetails?.cardDetails,isIntl])

    useEffect(() => {
        if (flightDetails.show) {
            document?.body?.classList?.add('stopScrolling')
            handlePerAdultPricing()
            fetchBenefitsForFlightDetails()
            checkSpecialRTStatus()
        }
        return () => {
            document?.body?.classList?.remove('stopScrolling')
        }
    }, [flightDetails, handlePerAdultPricing, fetchBenefitsForFlightDetails])

    const closeFlightDetails = () => {
        setFlightDetails({
            show: false,
            cardDetails: []
        })
    }

    const onContinueClick = () => {
        if(isIntl) {
            if(!isTripDetails && isRoundTrip) {
                // In this case CTA is 'DONE
                closeFlightDetails()
                return
            }
            onInternationalBookClick()
        } else {
            onDomesticTuppleClick(flightDetails?.cardDetails?.[0])
        }
        onDomesticTuppleClick(flightDetails?.cardDetails?.[0])
        closeFlightDetails()
    }

    // Helper function to extract baggage data for a specific journey
    const extractBaggageDataForJourney = (journeyData: any, cardIndex: number) => {
        if (isEmpty(journeyData)) return null

        const fareBenefitIds = Object.keys(journeyData?.fareBenefits || {})
        if (fareBenefitIds.length === 0) return null

        const fareBenefitId = fareBenefitIds[0]
        let baggageAllowanceId = journeyData?.fareBenefits[fareBenefitId]?.baggageAllowanceId || ''
        
        // Fallback to subTravelOptionBenefits if no direct baggageAllowanceId
        if (isEmpty(baggageAllowanceId)) {
            const subTravelOptionIds = flightDetails?.cardDetails?.[cardIndex]?.subTravelOptionIds
            if (subTravelOptionIds && subTravelOptionIds.length > 0) {
                baggageAllowanceId = journeyData?.fareBenefits[fareBenefitId]?.subTravelOptionBenefits[subTravelOptionIds[0]]?.benefits?.baggageAllowanceId
            }
        }

        if (isEmpty(baggageAllowanceId)) return null

        const baggageAllowances = journeyData?.baggageAllowances[baggageAllowanceId]
        return {
            benefitList: baggageAllowances?.benefitList || [],
            benefitSummary: baggageAllowances?.benefitSummary || []
        }
    }

    useEffect(() => {
        if (flightDetails.show && !isEmpty(flightBenefitsData)) {
            const newBaggageData: any = {}

            // Extract onward baggage data
            const onWardsBaggageData = extractBaggageDataForJourney(flightBenefitsData?.onWardsData, 0)
            if (onWardsBaggageData) {
                newBaggageData.onWardsBaggageData = onWardsBaggageData
            }

            // Extract return baggage data
            const returnBaggageData = extractBaggageDataForJourney(flightBenefitsData?.returnData, 1)
            if (returnBaggageData) {
                newBaggageData.returnBaggageData = returnBaggageData
            }

            setBaggageData(newBaggageData)
        }
    }, [flightBenefitsData, flightDetails.cardDetails])

    useEffect(() => {
            const triggerItinearyRavenEvent = () => {
                const faresList = getFaresInfo(flightDetails?.cardDetails?.[0], subTravelOptions, fares) || []
                const cheapestFare = Array.isArray(faresList) ? faresList.find((fare: any) => fare?.isCheapest) || faresList?.[0] : undefined
                // Build seat/meal from fare benefits
                const seatMeal: string[] = []
                const benefitIds: string[] = cheapestFare?.benefits?.benefitIds || []
                benefitIds.forEach((id: string) => {
                    const summary = (searchData as any)?.benefits?.[id]?.benefitSummary?.[0]
                    const bType = summary?.benefitType
                        if (bType === 'SEAT') seatMeal.push(summary?.text || 'Paid Seat')
                        if (bType === 'MEAL') seatMeal.push(summary?.text || 'Paid Meal')
                })
                // Build baggage from already extracted baggageData
                const baggageTexts: string[] = []
                const appendBaggage = (benefitSummary: any[] = []) => {
                    benefitSummary.forEach(({ benefitType, text, placeholderMap }) => {
                        const formatted = replacePlaceholders(text, placeholderMap).split('|')[0] || ''
                        const match = formatted.match(/(\d{1,4})\s?kg/i)
                        const weight = match ? `${match[1]} kg` : ''
                        if (benefitType === 'HAND_BAGGAGE' && weight) baggageTexts.push(`${weight} cabin`)
                        if (benefitType === 'CHECK_IN_BAGGAGE' && weight) baggageTexts.push(`${weight} check-in`)
                    })
                }
                appendBaggage(baggageData?.onWardsBaggageData?.benefitSummary)
                appendBaggage(baggageData?.returnBaggageData?.benefitSummary)
                const tagList = getIntlTuppleTags(cheapestFare) || []
                const tagTexts = (tagList || []).map((item: any) => item.displayText).filter(Boolean)
                const eventDescription2 = [...seatMeal, ...baggageTexts, ...tagTexts].filter(Boolean).join(' | ')

                const payload = generateSrpAdditionalPayload({
                    action_name: selectedTab === 1 ? 'tuple_detail_fare_rule_tab_click' : 'tuple_detail_itineary_tab_click',
                    action_type: 'click',
                    property: 'srp_page' + (isRoundTrip ? '_rt' : '_ow'),
                    listOfFares: getFaresInfo(flightDetails?.cardDetails?.[0], subTravelOptions, fares),
                    index: flightDetails?.cardDetails?.[0]?.cardId,
                    card: flightDetails?.cardDetails?.[0],
                    stops: flightDetails?.cardDetails?.[0]?.summary?.stops || 0,
                    searchData: searchData,
                    isSpecialRT: isSpecialRT
                })
                ravenSDKSrpCfwTrigger('a_ui_action', searchData, searchQuery as Partial<ProcessedData>, { ...payload, event_description2: eventDescription2 })
            }
            triggerItinearyRavenEvent()
    }, [selectedTab])

    return (
        <>
            <DrawerV2
                atTop={true}
                direction="right"
                handleClose={closeFlightDetails}
                isOpen={flightDetails.show}
                showOverlay={false}
                onExitEnd={() => {}}
            >
                <Container className="bg-white flex flex-column" style={{ width: '600px', height: '100vh' }}>
                    <FlightDetailsHeader closeFlightDetails={closeFlightDetails} searchQuery={searchQuery} isTripDetails={isTripDetails} />
                   { isIntl && (isTripDetails || !isRoundTrip) && <FlightDetailsTab
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                    />}
                    <Container className="flex-1 oy-scroll p-relative">
                        <FlightDetailsBody baggageAllowancesInfo={baggageData} cards={flightDetails.cardDetails} flightBenefitsData={flightBenefitsData} isIntl={isIntl} isIntlSpecialRT={isSpecialRT} isTripDetails={isTripDetails} searchQuery={searchQuery} selectedTab={selectedTab}/>
                    </Container>
                    { <FlightDetailsFooter
                        footerData={pricingDetails}
                        isIntl={isIntl}
                        isRoundTrip={isRoundTrip}
                        isTripDetails={isTripDetails}
                        perAdultTooltip={perAdultTooltip}
                        showPerAdultPricing={showPerAdultPricing}
                        onContinueClick={onContinueClick}
                    />}
                </Container>
            </DrawerV2>
            {/* {flightDetails.show && <Overlay customOverlayWrapperClass="z-50 p-fixed t-0 l-0 b-0 r-0" />} */}
        </>
    )
}

export default FlightDetails
